import { getLabel } from "scripts/components/get-label"
import { createLabelLayout } from "scripts/components/productLabels"
import {computePosition, flip, offset, Placement, shift} from "@floating-ui/dom";

const MOBILE_BREAKPOINT = 768
const GAP_BETWEEN_TRIGGER_AND_POPUP = 4
const CONTAINER_PADDING = 16

interface PopupConfig {
  triggerSelector: string
  containerSelector: string
  shouldBuildLayout: boolean
  popUpWidth: number
  allowedPlacements: Placement[]
  getPopupContainer: (trigger: HTMLElement) => HTMLElement | null
}

const labelPopupConfig: PopupConfig = {
  triggerSelector: "[data-label-id]",
  containerSelector: ".js_product_label_info",
  shouldBuildLayout: true,
  popUpWidth: 287,
  allowedPlacements: ["top", "bottom"],
  getPopupContainer: (trigger) => {
    return trigger.parentElement.querySelector(".js_product_label_info")
  },
}

const discountPopupConfig: PopupConfig = {
  triggerSelector: "button.js_toggle_discount",
  containerSelector: ".js_product_discount_info",
  shouldBuildLayout: false,
  popUpWidth: 252,
  allowedPlacements: ["top-end", "bottom-end"],
  getPopupContainer: (trigger) => {
    return trigger.closest(".product-card").querySelector(".js_product_discount_info")
  },
}

const initCustomPopupEvents = (config: PopupConfig) => {
  let clone: HTMLElement | null = null

  const hideDiscountInfo = () => {
    if (!clone) return

    clone.remove()
    clone = null

    document
      .querySelectorAll(config.containerSelector)
      .forEach((el) => el.classList.add("hide"))
    document.querySelectorAll("button.js_toggle_discount").forEach((el) => {
      el.classList.remove("active")
      el.setAttribute("aria-expanded", "false")
    })
  }

  document.addEventListener("click", async function (e) {
    const target = e.target as HTMLElement
    const isMobile = !window.matchMedia(`(min-width: ${MOBILE_BREAKPOINT}px)`).matches;

    if (target.closest(".product__discount_footer_link")) {
      requestAnimationFrame(() => hideDiscountInfo())
      return
    }

    const closeButton = target.closest(".js_close_discount")
    if (closeButton) {
      if (!isMobile) {
        const container = closeButton.closest(config.containerSelector)
        if (container) container.classList.add("hide")
      } else {
        document
          .querySelectorAll(config.containerSelector)
          .forEach((el) => el.classList.add("hide"))
      }
      document
        .querySelectorAll(config.triggerSelector)
        .forEach((el) => el.classList.remove("active"))
      if (clone) {
        clone.remove()
        clone = null
      }
      return
    }

    if (target.closest(config.containerSelector)) {
      if (isMobile && !target.closest(".js_prod_bg")) {
        hideDiscountInfo()
      }
      return
    }

    const triggerButton = target.closest(config.triggerSelector) as HTMLElement | null

    if (!triggerButton) {
      hideDiscountInfo()
      return
    }

    hideDiscountInfo()

    if (config.shouldBuildLayout) {
      if (!triggerButton.dataset.labelId) {
        return
      }
      const labelData = await getLabel(+triggerButton.dataset.labelId)
      if (!labelData) return
      createLabelLayout(triggerButton, labelData)
      if (!labelData.description && (!labelData.url || !labelData.link)) {
        return
      }
    }

    triggerButton.setAttribute("aria-expanded", "true")
    const container = config.getPopupContainer(triggerButton)
    document.querySelectorAll(config.triggerSelector).forEach((el) => {
      if (el !== triggerButton && el.classList.contains("active")) {
        el.classList.remove("active")
        const sibling = el.parentElement.querySelector(config.containerSelector)
        if (sibling) {
          sibling.classList.add("hide")
        }
      }
    })

    triggerButton.classList.toggle("active")
    container.classList.toggle("hide")

    if (clone) {
      clone.remove()
      clone = null
    }

    if (!container.classList.contains("hide")) {
      const newClone = container.cloneNode(true) as HTMLElement
      container.classList.add("hide")
      newClone.style.cssText = `width:${config.popUpWidth}px;visibility:hidden`
      document.body.appendChild(newClone)
      clone = newClone

      if (!isMobile) {
        computePosition(triggerButton, clone, {
          placement: config.allowedPlacements[0],
          strategy: "absolute",
          middleware: [
            offset(GAP_BETWEEN_TRIGGER_AND_POPUP),
            flip({
              fallbackPlacements: config.allowedPlacements.slice(1),
              rootBoundary: "viewport",
            }),
            shift({ padding: CONTAINER_PADDING }),
          ],
        }).then(({ x, y }) => {
          if (!clone) return
          Object.assign(clone.style, {
            position: "absolute",
            left: `${x}px`,
            top: `${y}px`,
            visibility: "visible",
          })
        })
      } else {
        Object.assign(clone.style, {
          position: "fixed",
          left: "0",
          top: "0",
          height: "100%",
          width: "100%",
          visibility: "visible",
        })
      }
    }
  })

  document.addEventListener(
    "scroll",
    () => {
      if (clone) hideDiscountInfo()
    },
    { passive: true },
  )

  document.addEventListener(
    "pointermove",
    (e) => {
      if (
        window.innerWidth >= MOBILE_BREAKPOINT &&
        clone &&
        e.buttons === 1 &&
        !(e.target as HTMLElement).closest(config.containerSelector)
      ) {
        hideDiscountInfo()
      }
    },
    { passive: true },
  )
}

export const initMiniCardsCustomPopups = () => {
  initCustomPopupEvents(labelPopupConfig)
  initCustomPopupEvents(discountPopupConfig)
}
